import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit, faTrash, faEye, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft, faChevronRight,
    faFilter, faCalendarAlt, faFilePdf, faPrint, faDownload, faCalendar,
    faTimesCircle, faBox, faTag, faBuilding, faBalanceScale, faDollarSign, faUndoAlt,
    faCalculator, faExclamationCircle, faHashtag, faPlus, faBarcode,
    faLayerGroup, faIndustry, faWeightHanging, faWarehouse, faFileExport,
    faChartLine, faPercent, faUser, faPalette, faRulerCombined, faBoxes as faBoxesIcon,
    faInfoCircle, faHistory, faStore, faExchangeAlt, faShoppingCart, faTruck,
    faShip, faRoad, faMoneyBillWave, faUniversity, faMoneyCheck, faCreditCard,
    faPhone, faEnvelope, faMapMarkerAlt, faGlobe, faClock, faCalendarCheck,
    faFileInvoice, faReceipt, faMoneyBill, faDownload as faDownloadIcon,
    faHourglassHalf, faCheckDouble, faMapMarkedAlt, faFlag, faClock as faClockIcon,
    faIdCard, faAddressCard, faComment, faMoneyCheckAlt, faBan, faBell, faMinus,
    faArrowsAlt, faExpand, faCompress, faMoneyBillTransfer, faUsers
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// ==============================================
// PO CUSTOMER TRANSACTIONS COMPONENT
// Customer Account Summary with Advanced Filters
// ==============================================

// Translator instance
const createPOCustomerTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_po_customer_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => cache.set(key, value));
            }
        } catch (e) { console.log("No cache found"); }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => { cacheObj[key] = value; });
            localStorage.setItem('ourzest_po_customer_translations', JSON.stringify(cacheObj));
        } catch (e) { console.error("Error saving to cache:", e); }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { console.log("Translation failed"); }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        title: 'Customer Transactions',
        customers: 'Customers',
        transactions: 'Transactions',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        searchPlaceholder: 'Search by customer name, phone, ID...',
        refresh: 'Refresh',
        loadingData: 'Loading transaction data...',
        noTransactionsFound: 'No transactions found',
        customerName: 'Customer Name',
        customerId: 'Customer ID',
        phone: 'Phone',
        currentBalance: 'Current Balance',
        totalPurchases: 'Total Purchases',
        totalPayments: 'Total Payments',
        transactionType: 'Type',
        amount: 'Amount',
        paymentMethod: 'Payment Method',
        transactionDate: 'Date',
        receiptNumber: 'Receipt No',
        referenceNumber: 'Reference',
        notes: 'Notes',
        actions: 'Actions',
        viewDetails: 'View Details',
        sale: 'Sale',
        payment: 'Payment',
        adjustment: 'Adjustment',
        refund: 'Refund',
        cash: 'Cash',
        bank_transfer: 'Bank Transfer',
        digital_wallet: 'Digital Wallet',
        card: 'Card',
        upi: 'UPI',
        filterByCustomer: 'Filter by Customer',
        selectCustomer: 'Select Customer',
        allCustomers: 'All Customers',
        filterByType: 'Transaction Type',
        allTypes: 'All Types',
        filterByPaymentMethod: 'Payment Method',
        allMethods: 'All Methods',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        amountRange: 'Amount Range',
        minAmount: 'Min Amount',
        maxAmount: 'Max Amount',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        exportToCSV: 'Export CSV',
        totalTransactions: 'Total Transactions',
        totalAmount: 'Total Amount',
        totalSales: 'Total Sales',
        totalPaymentsReceived: 'Total Payments',
        serialNo: 'S.No',
        close: 'Close',
        customerDetails: 'Customer Details',
        transactionHistory: 'Transaction History',
        balanceInfo: 'Balance Information',
        generatedBy: 'Generated by',
        reportDate: 'Report Date',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        filterByEmployee: 'Filter by Employee',
        allEmployees: 'All Employees',
        selectEmployee: 'Select Employee',
        employeeTransactions: 'Employee Transactions',
        showingTransactionsFor: 'Showing transactions for'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = Object.keys(defaultTexts).map(key =>
            translateText(defaultTexts[key], lang).then(translated => { translations[key] = translated; })
        );
        await Promise.all(promises);
        saveToCache();
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const poCustomerTranslator = createPOCustomerTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Type Badge Component
const TypeBadge = ({ type, translations }) => {
    const getTypeStyle = (type) => {
        switch (type) {
            case 'sale': return { bg: '#eef2ff', color: '#1e40af', border: '#1e40af40', icon: faShoppingCart };
            case 'payment': return { bg: '#e0f2fe', color: '#0369a1', border: '#0369a140', icon: faMoneyBillWave };
            case 'adjustment': return { bg: '#fef9c3', color: '#854d0e', border: '#854d0e40', icon: faCalculator };
            case 'refund': return { bg: '#ffe4e6', color: '#be123c', border: '#be123c40', icon: faUndoAlt };
            default: return { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faMoneyBill };
        }
    };

    const style = getTypeStyle(type);
    const label = translations?.[type] || type;

    return (
        <div className="type-badge" style={{
            backgroundColor: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '4px 8px',
            borderRadius: '20px',
            fontSize: '12px'
        }}>
            <FontAwesomeIcon icon={style.icon} size="xs" />
            {label}
        </div>
    );
};

// Format currency function - uses Rs. instead of $
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Rs. 0.00';
    return `Rs. ${parseFloat(amount).toFixed(2)}`;
};

// Main Component
const CustomerTransactionsReport = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    // State
    const [customers, setCustomers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerAccount, setCustomerAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Employee and Customer Filter states
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [filteredCustomersByEmployee, setFilteredCustomersByEmployee] = useState([]);
    const [employeeFilteredTransactions, setEmployeeFilteredTransactions] = useState([]);

    // Filter states
    const [filterCustomerId, setFilterCustomerId] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterAmountMin, setFilterAmountMin] = useState('');
    const [filterAmountMax, setFilterAmountMax] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // UI State
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('ourzest_theme') || '#8b5cf6');
    const [translations, setTranslations] = useState({});
    const [pdfLoading, setPdfLoading] = useState(false);

    const toastTimerRef = useRef(null);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await poCustomerTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch data
    useEffect(() => {
        if (user && user.username) {
            fetchCustomersAndTransactions();
            fetchAvailableShops();
            fetchEmployees();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated');
        }
    }, [user, currentShop]);

    // Fetch customers and transactions when employee is selected
    useEffect(() => {
        if (selectedEmployeeId) {
            fetchCustomersByEmployee(selectedEmployeeId);
            fetchTransactionsByEmployee(selectedEmployeeId);
            setFilterCustomerId('');
        } else {
            setFilteredCustomersByEmployee([]);
            setEmployeeFilteredTransactions([]);
            setFilterCustomerId('');
            // When no employee selected, reset to show all transactions
            applyFilters();
        }
    }, [selectedEmployeeId, currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [transactions, employeeFilteredTransactions, filterCustomerId, filterType, filterPaymentMethod, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, searchTerm, selectedEmployeeId]);

    const fetchEmployees = async () => {
        if (!currentShop) {
            console.log('No current shop, skipping employee fetch');
            return;
        }

        try {
            console.log('Fetching employees for shop:', currentShop.shop_id);
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_employees_list',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            console.log('Employees API response:', data);

            if (data.status === 'success') {
                const employeesList = data.data || [];
                console.log(`Found ${employeesList.length} employees`);
                setEmployees(employeesList);
                if (employeesList.length === 0) {
                    console.log('No active employees found for this shop');
                    showToast('warning', 'No employees found for this shop');
                }
            } else {
                console.error('Error from employees API:', data.message);
                setEmployees([]);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            setEmployees([]);
            showToast('error', 'Failed to load employees');
        }
    };

    const fetchCustomersByEmployee = async (employeeId) => {
        if (!currentShop || !employeeId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_po_customers_by_employee',
                    shop_id: currentShop.shop_id,
                    employee_id: employeeId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFilteredCustomersByEmployee(data.data || []);
            } else {
                setFilteredCustomersByEmployee([]);
            }
        } catch (error) {
            console.error('Error fetching customers by employee:', error);
            setFilteredCustomersByEmployee([]);
        }
    };

    const fetchTransactionsByEmployee = async (employeeId) => {
        if (!currentShop || !employeeId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_po_transactions_by_employee',
                    shop_id: currentShop.shop_id,
                    employee_id: employeeId,
                    limit: 1000
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setEmployeeFilteredTransactions(data.data || []);
            } else {
                setEmployeeFilteredTransactions([]);
            }
        } catch (error) {
            console.error('Error fetching transactions by employee:', error);
            setEmployeeFilteredTransactions([]);
            showToast('error', 'Failed to load employee transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchCustomersAndTransactions();
                fetchEmployees();
                setSelectedEmployeeId('');
                setFilterCustomerId('');
                setEmployeeFilteredTransactions([]);
                setFilteredCustomersByEmployee([]);
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            showToast('error', 'Failed to switch shop');
        }
    };

    const fetchCustomersAndTransactions = async () => {
        if (!currentShop) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const customersRes = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_po_all_customers_account',
                    shop_id: currentShop.shop_id
                })
            });
            const customersData = await customersRes.json();

            const transRes = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_po_transaction_history',
                    shop_id: currentShop.shop_id,
                    limit: 1000
                })
            });
            const transData = await transRes.json();

            if (customersData.status === 'success') {
                setCustomers(customersData.data || []);
            }
            if (transData.status === 'success') {
                setTransactions(transData.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showToast('error', 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        // Use employee-filtered transactions if employee is selected, otherwise use all transactions
        let filtered = selectedEmployeeId ? [...employeeFilteredTransactions] : [...transactions];

        if (filterCustomerId) {
            filtered = filtered.filter(t => t.customer_id == filterCustomerId);
        }

        if (filterType) {
            filtered = filtered.filter(t => t.transaction_type === filterType);
        }

        if (filterPaymentMethod) {
            filtered = filtered.filter(t => t.payment_method === filterPaymentMethod);
        }

        if (filterDateFrom) {
            filtered = filtered.filter(t => t.transaction_date >= filterDateFrom);
        }
        if (filterDateTo) {
            filtered = filtered.filter(t => t.transaction_date <= filterDateTo);
        }

        if (filterAmountMin) {
            filtered = filtered.filter(t => parseFloat(t.amount) >= parseFloat(filterAmountMin));
        }
        if (filterAmountMax) {
            filtered = filtered.filter(t => parseFloat(t.amount) <= parseFloat(filterAmountMax));
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t =>
                t.customer_name?.toLowerCase().includes(term) ||
                t.receipt_number?.toLowerCase().includes(term) ||
                t.reference_number?.toLowerCase().includes(term) ||
                t.transaction_id?.toLowerCase().includes(term)
            );
        }

        setFilteredTransactions(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSelectedEmployeeId('');
        setFilterCustomerId('');
        setFilterType('');
        setFilterPaymentMethod('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterAmountMin('');
        setFilterAmountMax('');
        setSearchTerm('');
        setEmployeeFilteredTransactions([]);
        setFilteredCustomersByEmployee([]);
    };

    const handleViewTransaction = async (transaction) => {
        setSelectedTransaction(transaction);
        try {
            const res = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_po_customer_account',
                    customer_id: transaction.customer_id,
                    shop_id: currentShop?.shop_id
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                setCustomerAccount(data.data);
            }
        } catch (error) {
            console.error('Error fetching customer account:', error);
        }
        setShowViewModal(true);
    };

    const exportToCSV = () => {
        if (filteredTransactions.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        const headers = ['ID', 'Customer Name', 'Customer ID', 'Type', 'Amount', 'Payment Method', 'Receipt No', 'Reference No', 'Transaction Date', 'Notes'];
        const rows = filteredTransactions.map(t => [
            t.id, t.customer_name, t.customer_id, t.transaction_type, t.amount,
            t.payment_method, t.receipt_number || '', t.reference_number || '',
            t.transaction_date, t.notes || ''
        ]);

        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast('success', 'CSV exported successfully');
    };

    const exportToPDF = async () => {
        if (filteredTransactions.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        setPdfLoading(true);
        try {
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();

            pdf.setFillColor(139, 92, 246);
            pdf.rect(0, 0, pageWidth, 40, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(18);
            pdf.text(translations.title || 'Customer Transactions', 14, 20);
            pdf.setFontSize(10);
            pdf.text(`${translations.generatedBy}: ${user?.username || 'System'}`, 14, 32);
            pdf.text(`${translations.reportDate}: ${new Date().toLocaleDateString()}`, pageWidth - 50, 32);

            const totalAmount = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount), 0);
            const totalSales = filteredTransactions.filter(t => t.transaction_type === 'sale').reduce((sum, t) => sum + parseFloat(t.amount), 0);
            const totalPayments = filteredTransactions.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + parseFloat(t.amount), 0);

            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(10);
            pdf.text(`${translations.totalTransactions}: ${filteredTransactions.length}`, 14, 55);
            pdf.text(`${translations.totalAmount}: ${formatCurrency(totalAmount)}`, 14, 62);
            pdf.text(`${translations.totalSales}: ${formatCurrency(totalSales)}`, 80, 55);
            pdf.text(`${translations.totalPaymentsReceived}: ${formatCurrency(totalPayments)}`, 80, 62);

            const tableData = filteredTransactions.map(t => [
                t.id,
                t.customer_name?.substring(0, 20) || '-',
                translations[t.transaction_type] || t.transaction_type,
                formatCurrency(parseFloat(t.amount)),
                translations[t.payment_method] || t.payment_method || '-',
                t.receipt_number || '-',
                t.transaction_date
            ]);

            autoTable(pdf, {
                startY: 70,
                head: [[translations.serialNo || 'ID', translations.customerName || 'Customer', translations.transactionType || 'Type', translations.amount || 'Amount', translations.paymentMethod || 'Method', translations.receiptNumber || 'Receipt', translations.transactionDate || 'Date']],
                body: tableData.map((row, idx) => [idx + 1, ...row]),
                theme: 'striped',
                headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
                styles: { fontSize: 8, cellPadding: 3 },
                columnStyles: { 0: { cellWidth: 15 }, 1: { cellWidth: 40 }, 2: { cellWidth: 25 }, 3: { cellWidth: 25 }, 4: { cellWidth: 30 }, 5: { cellWidth: 35 }, 6: { cellWidth: 25 } }
            });

            pdf.save(`transactions_${new Date().toISOString().split('T')[0]}.pdf`);
            showToast('success', 'PDF exported successfully');
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch { return dateString; }
    };

    // Get today's date for date inputs
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Calculate totals
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
    const totalAmount = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount), 0);
    const totalSales = filteredTransactions.filter(t => t.transaction_type === 'sale').reduce((sum, t) => sum + parseFloat(t.amount), 0);
    const totalPayments = filteredTransactions.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + parseFloat(t.amount), 0);
    const totalTransactionsCount = filteredTransactions.length;

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Get customers to display (filtered by employee or all)
    const displayCustomers = selectedEmployeeId ? filteredCustomersByEmployee : customers;

    // Get selected employee name
    const selectedEmployee = employees.find(emp => emp.employeeid == selectedEmployeeId);

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faMoneyBillTransfer} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.title || 'Customer Transactions'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : translations.noShopSelected || 'No shop selected'}
                            </p>
                        </div>
                    </div>
                </div>

               {/* Shop Indicator */}
{!currentShop ? (
    <div className="shop-indicator-modern no-shop">
        <div className="shop-badge">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="shop-warning">
                {translations.noShopSelected || 'No shop selected'}
            </span>
        </div>

        <button
            className="btn-modern btn-switch-shop"
            onClick={() => setShowShopSelector(true)}
        >
            <FontAwesomeIcon icon={faStore} />
            <span>{translations.switchShop || 'Switch Shop'}</span>
        </button>
    </div>
) : null}
</div>

            {/* Active Employee Filter Indicator */}
                {selectedEmployeeId && selectedEmployee && (
                    <div className="active-filter-indicator" style={{
                        padding: '12px 20px',
                        backgroundColor: '#e0e7ff',
                        borderRadius: '12px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        border: '1px solid #c7d2fe'
                    }}>
                        <FontAwesomeIcon icon={faUser} style={{ color: '#4f46e5', fontSize: '18px' }} />
                        <span style={{ color: '#1e1b4b', fontWeight: '500' }}>
                            {translations.showingTransactionsFor || 'Showing transactions for'}: <strong>{selectedEmployee.employee_name}</strong>
                            {selectedEmployee.position && ` (${selectedEmployee.position})`}
                        </span>
                        <button
                            onClick={() => setSelectedEmployeeId('')}
                            style={{
                                marginLeft: 'auto',
                                background: '#fee2e2',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#dc2626',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            Clear Filter
                        </button>
                    </div>
                )}

                {/* Summary Stats Section */}
                {currentShop && filteredTransactions.length > 0 && (
                    <div className="summary-stats-modern">
                        <div className="stat-card">
                            <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                                <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#10b981' }} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{totalTransactionsCount}</div>
                                <div className="stat-label">
                                    {selectedEmployeeId ? 'Employee Transactions' : translations.totalTransactions || 'Total Transactions'}
                                </div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                                <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#3b82f6' }} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{formatCurrency(totalAmount)}</div>
                                <div className="stat-label">{translations.totalAmount || 'Total Amount'}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                                <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#8b5cf6' }} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{formatCurrency(totalSales)}</div>
                                <div className="stat-label">{translations.totalSales || 'Total Sales'}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                                <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#f59e0b' }} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{formatCurrency(totalPayments)}</div>
                                <div className="stat-label">{translations.totalPaymentsReceived || 'Total Payments'}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Advanced Filters Section */}
                <div className="advanced-filters-section">
                    <div className="filters-row">
                        {/* Employee Filter */}
                        <div className="filter-group compact">
                            <select
                                value={selectedEmployeeId}
                                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                <option value="">{translations.allEmployees || 'All Employees'}</option>
                                {employees.map(emp => (
                                    <option key={emp.employeeid} value={emp.employeeid}>
                                        {emp.employee_name} {emp.position ? `(${emp.position})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Customer Filter - Filtered by selected employee */}
                        <div className="filter-group compact">
                            <select
                                value={filterCustomerId}
                                onChange={(e) => setFilterCustomerId(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                                disabled={selectedEmployeeId && displayCustomers.length === 0}
                            >
                                <option value="">{translations.allCustomers || 'All Customers'}</option>
                                {displayCustomers.map(c => (
                                    <option key={c.customer_id} value={c.customer_id}>
                                        {c.customer_name} (ID: {c.customer_id})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Transaction Type Filter */}
                        <div className="filter-group compact">
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                <option value="">{translations.allTypes || 'All Types'}</option>
                                <option value="sale">{translations.sale || 'Sale'}</option>
                                <option value="payment">{translations.payment || 'Payment'}</option>
                                <option value="adjustment">{translations.adjustment || 'Adjustment'}</option>
                                <option value="refund">{translations.refund || 'Refund'}</option>
                            </select>
                        </div>

                        {/* Payment Method Filter */}
                        <div className="filter-group compact">
                            <select
                                value={filterPaymentMethod}
                                onChange={(e) => setFilterPaymentMethod(e.target.value)}
                                className="filter-select"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                <option value="">{translations.allMethods || 'All Methods'}</option>
                                <option value="cash">{translations.cash || 'Cash'}</option>
                                <option value="bank_transfer">{translations.bank_transfer || 'Bank Transfer'}</option>
                                <option value="digital_wallet">{translations.digital_wallet || 'Digital Wallet'}</option>
                                <option value="card">{translations.card || 'Card'}</option>
                                <option value="upi">{translations.upi || 'UPI'}</option>
                            </select>
                        </div>

                        {/* Date Range */}
                        <div className="filter-group compact date-filter">
                            <input
                                type="date"
                                value={filterDateFrom}
                                onChange={(e) => setFilterDateFrom(e.target.value)}
                                max={getTodayDate()}
                                className="filter-input compact date-input"
                                placeholder={translations.startDate || 'Start Date'}
                                title={translations.startDate || 'Start Date'}
                            />
                        </div>
                        <div className="filter-group compact date-filter">
                            <input
                                type="date"
                                value={filterDateTo}
                                onChange={(e) => setFilterDateTo(e.target.value)}
                                min={filterDateFrom}
                                max={getTodayDate()}
                                className="filter-input compact date-input"
                                placeholder={translations.endDate || 'End Date'}
                                title={translations.endDate || 'End Date'}
                            />
                        </div>

                        {/* Amount Range */}
                        <div className="filter-group compact">
                            <input
                                type="number"
                                value={filterAmountMin}
                                onChange={(e) => setFilterAmountMin(e.target.value)}
                                placeholder={translations.minAmount || 'Min Amount'}
                                className="filter-input compact"
                                step="0.01"
                                min="0"
                            />
                        </div>
                        <div className="filter-group compact">
                            <input
                                type="number"
                                value={filterAmountMax}
                                onChange={(e) => setFilterAmountMax(e.target.value)}
                                placeholder={translations.maxAmount || 'Max Amount'}
                                className="filter-input compact"
                                step="0.01"
                                min="0"
                            />
                        </div>

                        {/* Search Box */}
                        <div className="filter-group compact search-group">
                            <div className="search-box compact">
                                <input
                                    type="text"
                                    placeholder={translations.searchPlaceholder || 'Search...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="filter-group compact">
                            <button
                                onClick={applyFilters}
                                className="refresh-btn-modern compact"
                                style={{ background: 'var(--primary-gradient, linear-gradient(135deg, #8b5cf6, #7c3aed))' }}
                            >
                                <FontAwesomeIcon icon={faFilter} />
                                <span className="btn-text">{translations.applyFilters || 'Apply'}</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button
                                onClick={resetFilters}
                                className="refresh-btn-modern compact"
                                style={{ background: '#6b7280' }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span className="btn-text">{translations.resetFilters || 'Reset'}</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button
                                onClick={exportToCSV}
                                className="refresh-btn-modern compact"
                                style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                                <span className="btn-text">CSV</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button
                                onClick={exportToPDF}
                                className="refresh-btn-modern compact"
                                disabled={pdfLoading || filteredTransactions.length === 0}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                            >
                                {pdfLoading ? (
                                    <div className="btn-spinner-small"></div>
                                ) : (
                                    <FontAwesomeIcon icon={faFilePdf} />
                                )}
                                <span className="btn-text">{translations.exportToPDF || 'PDF'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner-modern"></div>
                        <p>{translations.loadingData || 'Loading transaction data...'}</p>
                    </div>
                ) : !currentShop ? (
                    <div className="no-shop-container">
                        <div className="no-shop-message">
                            <FontAwesomeIcon icon={faStore} />
                            <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                            <p>{translations.selectShop || 'Please select a shop to view transactions'}</p>
                            <button
                                className="btn-modern btn-primary-modern"
                                onClick={() => setShowShopSelector(true)}
                            >
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Table Section */}
                        <div className="table-scroll-container">
                            <div className="table-container-modern">
                                <div className="customer-table-wrapper">
                                    <table className="customer-table-modern">
                                        <thead>
                                            <tr>
                                                <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                                <th className="centered-cell">{translations.customerName || 'Customer'}</th>
                                                <th className="centered-cell">{translations.transactionType || 'Type'}</th>
                                                <th className="centered-cell">{translations.amount || 'Amount'}</th>
                                                <th className="centered-cell">{translations.paymentMethod || 'Payment Method'}</th>
                                                <th className="centered-cell">{translations.receiptNumber || 'Receipt No'}</th>
                                                <th className="centered-cell">{translations.transactionDate || 'Date'}</th>
                                                <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentTransactions.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" className="centered-cell">
                                                        <div className="no-data-message">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                            <p>
                                                                {selectedEmployeeId
                                                                    ? `No transactions found for ${selectedEmployee?.employee_name}`
                                                                    : translations.noTransactionsFound || 'No transactions found'}
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                currentTransactions.map((tx, idx) => (
                                                    <tr key={tx.id}>
                                                        <td className="id-cell centered-cell">
                                                            <span className="cell-text">{startIndex + idx + 1}</span>
                                                        </td>
                                                        <td className="centered-cell">
                                                            <div className="customer-info-cell">
                                                                <span className="cell-text customer-name">{tx.customer_name}</span>
                                                                <small className="customer-id">ID: {tx.customer_id}</small>
                                                            </div>
                                                        </td>
                                                        <td className="centered-cell">
                                                            <TypeBadge type={tx.transaction_type} translations={translations} />
                                                        </td>
                                                        <td className="centered-cell">
                                                            <span className={`amount-text ${tx.transaction_type === 'payment' ? 'amount-negative' : 'amount-positive'}`}>
                                                                {tx.transaction_type === 'payment' ? '-' : '+'} {formatCurrency(parseFloat(tx.amount))}
                                                            </span>
                                                        </td>
                                                        <td className="centered-cell">
                                                            <span className="cell-text">{translations[tx.payment_method] || tx.payment_method || '-'}</span>
                                                        </td>
                                                        <td className="centered-cell">
                                                            <span className="cell-text receipt-number">{tx.receipt_number || tx.reference_number || '-'}</span>
                                                        </td>
                                                        <td className="centered-cell">
                                                            <span className="cell-text">{formatDate(tx.transaction_date)}</span>
                                                        </td>
                                                        <td className="actions-cell-modern centered-cell">
                                                            <div className="actions-content">
                                                                <button
                                                                    onClick={() => handleViewTransaction(tx)}
                                                                    className="action-btn-modern view-btn-modern"
                                                                    title={translations.viewDetails || 'View Details'}
                                                                >
                                                                    <FontAwesomeIcon icon={faEye} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Pagination */}
                        {filteredTransactions.length > itemsPerPage && currentShop && (
                            <div className="pagination-modern">
                                <div className="pagination-info">
                                    <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTransactions.length)} {translations.of || 'of'} {filteredTransactions.length} {translations.transactions || 'transactions'}</span>
                                    {currentShop?.shop_name && (
                                        <span className="shop-info-pagination">
                                            | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                        </span>
                                    )}
                                    <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>
                                        {translations.totalAmount || 'Total Amount'}: {formatCurrency(totalAmount)}
                                    </span>
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="pagination-btn"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                        <span>{translations.previous || 'Previous'}</span>
                                    </button>

                                    <div className="page-numbers">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="pagination-btn"
                                    >
                                        <span>{translations.next || 'Next'}</span>
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* View Transaction Modal */}
                {showViewModal && selectedTransaction && (
                    <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-modern">
                                <div className="modal-icon">
                                    <FontAwesomeIcon icon={faMoneyBillTransfer} />
                                </div>
                                <div className="modal-title">
                                    <h3>{translations.transactionDetails || 'Transaction Details'}</h3>
                                    <span className="transaction-id-badge">#{selectedTransaction.id}</span>
                                </div>
                                <button className="close-btn-modern" onClick={() => setShowViewModal(false)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <div className="modal-body-modern view-modal-body">
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faUser} />{translations.customerDetails || 'Customer Details'}</h4>
                                    <div className="view-grid">
                                        <div className="view-row"><span className="view-label">{translations.customerName || 'Customer Name'}:</span><span className="view-value">{selectedTransaction.customer_name}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.customerId || 'Customer ID'}:</span><span className="view-value">{selectedTransaction.customer_id}</span></div>
                                    </div>
                                </div>

                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faMoneyBill} />{translations.transactionHistory || 'Transaction Details'}</h4>
                                    <div className="view-grid">
                                        <div className="view-row"><span className="view-label">{translations.transactionType || 'Type'}:</span><span className="view-value"><TypeBadge type={selectedTransaction.transaction_type} translations={translations} /></span></div>
                                        <div className="view-row"><span className="view-label">{translations.amount || 'Amount'}:</span><span className="view-value total-amount" style={{ color: selectedTransaction.transaction_type === 'payment' ? '#059669' : '#dc2626' }}>{formatCurrency(parseFloat(selectedTransaction.amount))}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.paymentMethod || 'Payment Method'}:</span><span className="view-value">{translations[selectedTransaction.payment_method] || selectedTransaction.payment_method || '-'}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.transactionDate || 'Date'}:</span><span className="view-value">{formatDate(selectedTransaction.transaction_date)}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.receiptNumber || 'Receipt Number'}:</span><span className="view-value">{selectedTransaction.receipt_number || '-'}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.referenceNumber || 'Reference Number'}:</span><span className="view-value">{selectedTransaction.reference_number || '-'}</span></div>
                                    </div>
                                </div>

                                {customerAccount && (
                                    <div className="view-section">
                                        <h4 className="view-section-title"><FontAwesomeIcon icon={faBalanceScale} />{translations.balanceInfo || 'Balance Information'}</h4>
                                        <div className="balance-info-card">
                                            <div className="view-grid">
                                                <div className="view-row"><span className="view-label">{translations.currentBalance || 'Current Balance'}:</span><span className="view-value balance-value">{formatCurrency(parseFloat(customerAccount.current_balance || 0))}</span></div>
                                                <div className="view-row"><span className="view-label">{translations.totalPurchases || 'Total Purchases'}:</span><span className="view-value">{formatCurrency(parseFloat(customerAccount.total_purchases || 0))}</span></div>
                                                <div className="view-row"><span className="view-label">{translations.totalPayments || 'Total Payments'}:</span><span className="view-value">{formatCurrency(parseFloat(customerAccount.total_payments || 0))}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedTransaction.notes && (
                                    <div className="view-section">
                                        <h4 className="view-section-title"><FontAwesomeIcon icon={faComment} />{translations.notes || 'Notes'}</h4>
                                        <div className="remarks-box">{selectedTransaction.notes}</div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer-modern">
                                <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>
                                    {translations.close || 'Close'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            );
};

            export default CustomerTransactionsReport;